import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import React from "react";
import Modal, { CommonModalProps } from "..";
import { produce } from "immer";
import { cacheService, setupService } from "../../../Core/Api/services";
import FormSection from "../../Forms/FormSection/form-section";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { DbDeliveryBroadcasterInfos } from "../../../Core/Api/Types/db-delivery-broadcaster-infos";
import { OrderProductType } from "../../../Core/Api/Types/order-product-type";
import { TranskodeVideoProfile } from "../../../Core/Api/Types/transkode-video-profile";
import BroadcasterLinkedFieldsTable from "../../Table/BroadcasterLinkedFieldsTable/broadcaster-linked-fields-table";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { DbMetadataFieldInfos } from "../../../Core/Api/Types/db-metadata-field-infos";
import { useTranslation } from "react-i18next";

type CloseClbProps = {
  confirm: boolean;
};

export type ModalBroadcasterElementProps = {
  type: "Modify" | "Create";
  inputBroadcaster?: DbDeliveryBroadcasterInfos;
  onClose?: (props: CloseClbProps) => void;
  onConfirmModalBroadcaster?: (broadcaster: DbDeliveryBroadcasterInfos, id?: number) => void;
};
export type ModalBroadcasterProps = CommonModalProps<ModalBroadcasterElementProps>;

const ModalBroadcaster = React.forwardRef<any, ModalBroadcasterProps>((props, ref) => {
  const { t } = useTranslation();
  const { closeModal, onClose, onConfirmModalBroadcaster, type, inputBroadcaster, ...modalProps } = props;

  const [broadcaster, setBroadcaster] = React.useState<DbDeliveryBroadcasterInfos>(
    new DbDeliveryBroadcasterInfos({ enabled: false, isAutomatedPipeline: false })
  );
  const [videoProfile, setVideoProfile] = React.useState<TranskodeVideoProfile>();
  const [xmlProfilePath, setXmlProfilePath] = React.useState<string>("");
  const [territoriesCodes, setTerritoriesCodes] = React.useState<string>("");
  const [languagesCodes, setLanguagesCodes] = React.useState<string>("");
  const [selectedField, setSelectedField] = React.useState<DbMetadataFieldInfos | null>(null);
  const enqueueCustomSnackbar = useCustomSnackbar();

  const handleCancelClick = () => {
    onClose && onClose({ confirm: false });
    closeModal();
  };

  const onClickAddField = () => {
    if (selectedField) {
      setBroadcaster(
        produce((draft) => {
          const fieldToAdd = {
            fieldId: selectedField.id,
            isMandatory: false,
            maxSynoLongChars: 0,
            maxSynoShortChars: 0,
          };
          let match = draft.metadataFieldsIncluded;
          if (match) match.push(fieldToAdd);
          else draft.metadataFieldsIncluded = [fieldToAdd];
        })
      );
      setSelectedField(null);
    }
  };

  const onChangeMetadataFields = (value: any, id: number, field: any) => {
    setBroadcaster(
      produce((draft) => {
        const match = draft.metadataFieldsIncluded?.find((x) => x.fieldId === id);
        match && console.log(match[field]);
        if (match) match[field] = value;
      })
    );
  };

  const onRemoveRow = (id: number, index: number) => {
    setBroadcaster(
      produce((draft) => {
        if (draft.metadataFieldsIncluded !== null)
          draft.metadataFieldsIncluded = draft.metadataFieldsIncluded?.filter((x) => x.fieldId !== id) || [];
      })
    );
  };

  const handleConfirmClick = () => {
    if (broadcaster?.name) {
      if (type === "Create")
        setupService.CreateBroadcaster(broadcaster).then(function (response) {
          if (response.success) {
            enqueueCustomSnackbar(t("alert:success.newBroadcasterCreated"), "success");
            onConfirmModalBroadcaster && onConfirmModalBroadcaster(broadcaster, response.objectId!);
            closeModal();
          } else {
            enqueueCustomSnackbar(response.message ?? "alert:somethingWentWrong", "error");
          }
        });
      if (type === "Modify")
        setupService.UpdateBroadcaster(broadcaster).then(function (response) {
          if (response.success) {
            enqueueCustomSnackbar(t("alert:success.broadcasterUpdated"), "success");
            onConfirmModalBroadcaster && onConfirmModalBroadcaster(broadcaster);
            closeModal();
          } else {
            enqueueCustomSnackbar(response.message ?? "alert:somethingWentWrong", "error");
          }
        });
    }
  };

  React.useEffect(() => {
    if (inputBroadcaster) setBroadcaster(inputBroadcaster);
  }, []);

  return (
    <Modal
      {...modalProps}
      title={t("modal:title.broadcaster", { type })}
      reference={ref}
      closeModal={handleCancelClick}
      width={1000}
      footer={
        <Stack direction="row" justifyContent="space-between">
          <Button onClick={handleCancelClick}>{t("common:cancel")}</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleConfirmClick}
            disabled={!broadcaster.name || !broadcaster.externalId || !broadcaster.description}
          >
            {type}
          </Button>
        </Stack>
      }
    >
      <Grid container spacing={2}>
        <FormSection title={t("common:generalInformationsLowerCase")} status="todo" sx={{ mb: 2, ml: 3, mt: 3 }}>
          <Grid container spacing={2}>
            <Grid item md={4}>
              <InputLabel sx={{ mb: 1 }}>{t("common:name")}</InputLabel>
              <OutlinedInput
                value={broadcaster?.name || ""}
                id={"name"}
                sx={{ width: "100%", mb: 2 }}
                onChange={(e) =>
                  setBroadcaster(
                    produce((draft) => {
                      draft!.name = e.target.value;
                    })
                  )
                }
              />
            </Grid>
            <Grid item md={2}>
              <InputLabel sx={{ mb: 1 }}>{t("field:label.externalID")}</InputLabel>
              <OutlinedInput
                value={broadcaster?.externalId || ""}
                id={"externalId"}
                sx={{ width: "100%", mb: 2 }}
                onChange={(e) =>
                  setBroadcaster(
                    produce((draft) => {
                      draft!.externalId = e.target.value;
                    })
                  )
                }
              />
            </Grid>
            <Grid item md={4}>
              <InputLabel sx={{ mb: 1 }}>{t("common:description")}</InputLabel>
              <OutlinedInput
                value={broadcaster?.description || ""}
                id={"description"}
                sx={{ width: "100%" }}
                onChange={(e) =>
                  setBroadcaster(
                    produce((draft) => {
                      draft!.description = e.target.value;
                    })
                  )
                }
              />
            </Grid>
            <Grid item md={2}>
              <InputLabel sx={{ mb: 1 }}>{t("field:label.automated")}</InputLabel>
              <Switch
                size="small"
                checked={broadcaster.isAutomatedPipeline || false}
                onChange={(e) =>
                  setBroadcaster(
                    produce((draft) => {
                      draft!.isAutomatedPipeline = e.target.checked;
                    })
                  )
                }
              />
            </Grid>
            <Grid item md={3}>
              <InputLabel sx={{ mb: 1 }}>{t("field:label.localTeritoryCode")}</InputLabel>
              <OutlinedInput
                value={broadcaster?.localizationTerritoryCode || ""}
                id={"localizationTerritoryCode"}
                sx={{ width: "100%" }}
                onChange={(e) =>
                  setBroadcaster(
                    produce((draft) => {
                      draft!.localizationTerritoryCode = e.target.value;
                    })
                  )
                }
              />
            </Grid>
            <Grid item md={3}>
              <InputLabel sx={{ mb: 1 }}>{t("field:label.serviceDescription")}</InputLabel>
              <OutlinedInput
                value={broadcaster?.serviceDescription || ""}
                id={"serviceDescription"}
                sx={{ width: "100%" }}
                onChange={(e) =>
                  setBroadcaster(
                    produce((draft) => {
                      draft!.serviceDescription = e.target.value;
                    })
                  )
                }
              />
            </Grid>
            <Grid item md={4}>
              <InputLabel sx={{ mb: 1 }}>{t("field:label.productType")}</InputLabel>
              <Select
                displayEmpty
                value={broadcaster.productType || ""}
                fullWidth
                MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
                onChange={(e) =>
                  setBroadcaster(
                    produce((draft) => {
                      draft!.productType = OrderProductType[e.target.value];
                    })
                  )
                }
              >
                {Object.keys(OrderProductType).map((type) => (
                  <MenuItem value={type} key={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item md={2}>
              <InputLabel sx={{ mb: 1 }}>{t("common:enable")}</InputLabel>
              <Switch
                size="small"
                checked={broadcaster.enabled || false}
                onChange={(e) =>
                  setBroadcaster(
                    produce((draft) => {
                      draft!.enabled = e.target.checked;
                    })
                  )
                }
              />
            </Grid>
            <Grid item md={12}>
              <InputLabel sx={{ mb: 1 }}>{t("field:label.documentFolderPath")}</InputLabel>
              <OutlinedInput
                value={broadcaster?.documentsFolder || ""}
                id={"documentsPath"}
                sx={{ width: "100%" }}
                onChange={(e) =>
                  setBroadcaster(
                    produce((draft) => {
                      draft!.documentsFolder = e.target.value;
                    })
                  )
                }
              />
            </Grid>
          </Grid>
        </FormSection>

        <FormSection title={t("field:label.identifyProfilesDefine")} sx={{ ml: 3 }} status="todo">
          <Grid container spacing={2}>
            <Grid item md={5}>
              <InputLabel sx={{ mb: 1 }}>{t("field:label.videoProfile")}</InputLabel>
              <Select
                displayEmpty
                MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
                value={videoProfile || ""}
                onChange={(e) => setVideoProfile(TranskodeVideoProfile[e.target.value])}
                fullWidth
              >
                {Object.keys(TranskodeVideoProfile).map((type) => (
                  <MenuItem value={type} key={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item md={6}>
              <InputLabel sx={{ mb: 1 }}>{t("field:label.xmlProfilePath")}</InputLabel>
              <OutlinedInput
                value={xmlProfilePath || ""}
                id={"xmlProfilePath"}
                sx={{ width: "100%", mb: 2 }}
                onChange={(e) => {
                  setXmlProfilePath(e.target.value);
                }}
              />
            </Grid>
            <Grid item md={1}>
              <Button
                sx={{ mt: 3.5 }}
                fullWidth
                variant="contained"
                disabled={
                  !videoProfile ||
                  xmlProfilePath === "" ||
                  broadcaster.identityProfiles?.find((x) => x.videoProfile === videoProfile) !== undefined ||
                  broadcaster.identityProfiles?.find((x) => x.xmlProfilePath === xmlProfilePath) !== undefined
                }
                onClick={() => {
                  setBroadcaster(
                    produce((draft) => {
                      if (draft!.identityProfiles === null)
                        draft!.identityProfiles = [
                          {
                            videoProfile: videoProfile!,
                            xmlProfilePath: xmlProfilePath,
                            id: -500,
                            isNew: true,
                            invalidMessage: null,
                            invalidStack: null,
                            isValidParsing: true,
                          },
                        ];
                      else
                        draft!.identityProfiles?.push({
                          videoProfile: videoProfile!,
                          xmlProfilePath: xmlProfilePath,
                          id: -500,
                          isNew: true,
                          invalidMessage: null,
                          invalidStack: null,
                          isValidParsing: true,
                        });
                    })
                  );
                  setXmlProfilePath("");
                  setVideoProfile(undefined);
                }}
              >
                {t("common:add")}
              </Button>
            </Grid>
            {broadcaster.identityProfiles?.map((profile, index) => (
              <React.Fragment key={index}>
                <Grid item md={4}>
                  <Chip label={profile.videoProfile} />
                </Grid>
                <Grid item md={7}>
                  <Tooltip title={profile.xmlProfilePath}>
                    <Chip label={profile.xmlProfilePath} />
                  </Tooltip>
                </Grid>
                <Grid item md={1}>
                  <Button
                    color="error"
                    fullWidth
                    size="small"
                    onClick={() =>
                      setBroadcaster(
                        produce((draft) => {
                          draft!.identityProfiles! = draft!.identityProfiles!.filter(
                            (x) =>
                              x.videoProfile !== profile.videoProfile && x.xmlProfilePath !== profile.xmlProfilePath
                          );
                        })
                      )
                    }
                  >
                    {t("common:delete")}
                  </Button>
                </Grid>
              </React.Fragment>
            ))}
          </Grid>
        </FormSection>
        <FormSection title={t("form:section.title.codes")} status="todo" sx={{ mb: 2, ml: 3, mt: 2 }}>
          <Grid container spacing={2}>
            <Grid item md={6}>
              <InputLabel>{t("field:label.territoryCode")}</InputLabel>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {t("modal:modalBroadcaster.body.addManyTerritoryCode")} "|".
              </Typography>
              <Stack direction={"row"} spacing={1} sx={{ flexWrap: "wrap", gap: 0.5, my: 1 }}>
                {broadcaster.territoriesCodes?.map((x) => (
                  <Chip
                    label={x}
                    key={x}
                    onDelete={() =>
                      setBroadcaster(
                        produce((draft) => {
                          draft.territoriesCodes! = draft.territoriesCodes!.filter((y) => y !== x);
                        })
                      )
                    }
                  />
                ))}
              </Stack>
              <OutlinedInput
                value={territoriesCodes || ""}
                id={"territoriesCodes"}
                sx={{ width: "100%", mb: 2, mt: 2 }}
                onChange={(e) => {
                  setTerritoriesCodes(e.target.value);
                }}
                minRows={5}
              />
              <Button
                disabled={territoriesCodes === ""}
                onClick={() => {
                  setBroadcaster(
                    produce((draft) => {
                      if (draft!.territoriesCodes === null) draft!.territoriesCodes = territoriesCodes.split("|");
                      else
                        draft!.territoriesCodes = Array.from(
                          new Set(draft!.territoriesCodes.concat(territoriesCodes.split("|")))
                        );
                    })
                  );
                  setTerritoriesCodes("");
                }}
              >
                {t("button:validateTerritoryCode")}
              </Button>
            </Grid>
            <Grid item md={6}>
              <InputLabel>{t("field:label.languageCodes")}</InputLabel>
              <Typography variant="body2" sx={{ mb: 2 }}>
                {t("modal:modalBroadcaster.body.addManyTerritoryCode")} "|".
              </Typography>
              <Stack direction={"row"} spacing={1} sx={{ flexWrap: "wrap", gap: 0.5, my: 1 }}>
                {broadcaster.languagesCodes?.map((x) => (
                  <Chip
                    label={x}
                    key={x}
                    onDelete={() =>
                      setBroadcaster(
                        produce((draft) => {
                          draft.languagesCodes! = draft.languagesCodes!.filter((y) => y !== x);
                        })
                      )
                    }
                  />
                ))}
              </Stack>
              <OutlinedInput
                value={languagesCodes || ""}
                id={"languagesCodes"}
                sx={{ width: "100%", mb: 2, mt: 2 }}
                onChange={(e) => {
                  setLanguagesCodes(e.target.value);
                }}
                minRows={5}
              />
              <Button
                disabled={languagesCodes === ""}
                onClick={() => {
                  setBroadcaster(
                    produce((draft) => {
                      if (draft!.languagesCodes === null) draft!.languagesCodes = languagesCodes.split("|");
                      else
                        draft!.languagesCodes = Array.from(
                          new Set(draft!.languagesCodes.concat(languagesCodes.split("|")))
                        );
                    })
                  );
                  setLanguagesCodes("");
                }}
              >
                {t("button:validateLanguageCode")}
              </Button>
            </Grid>
          </Grid>
        </FormSection>
        {broadcaster.productType === OrderProductType.TranscodeBundleByProfile && (
          <FormSection title={t("field:label.linkedMetadataField")} sx={{ ml: 3 }} status="todo">
            <BroadcasterLinkedFieldsTable
              rows={broadcaster.metadataFieldsIncluded?.map((x) => ({ ...x, id: x.fieldId })) || []}
              onRemoveRow={onRemoveRow}
              onChange={onChangeMetadataFields}
            />
            <Stack display="flex" direction="row" mt={2} justifyContent="flex-end">
              <Autocomplete
                sx={{ width: "300px" }}
                onChange={(e, v) => setSelectedField(v)}
                popupIcon={<ExpandMoreRoundedIcon />}
                getOptionLabel={(option: any) => option.label}
                value={selectedField}
                renderInput={(params) => (
                  <OutlinedInput
                    sx={{ width: "100%" }}
                    {...params.InputProps}
                    inputProps={params.inputProps}
                    placeholder={t("field:placeholder.selectAnyField")}
                  />
                )}
                options={cacheService
                  .GetMetadata()
                  .filter((x) => !broadcaster.metadataFieldsIncluded?.find((y) => y.fieldId === x.id))}
              />
              <Button
                disabled={!selectedField?.id}
                onClick={onClickAddField}
                sx={{ width: "100px", ml: 2 }}
                variant="outlined"
              >
                {t("common:add")}
              </Button>
            </Stack>
          </FormSection>
        )}
      </Grid>
    </Modal>
  );
});

export default ModalBroadcaster;
